import { normalizeId } from './ids';

/**
 * The template vocabulary each rule kind may use, as a TREE for TokenInput's
 * pill dropdown.
 *
 * Two vocabularies, and the difference is not cosmetic. A SCHEDULE rule has no
 * trigger item — there is nothing on a clock for `{{trigger.*}}` to mean — and
 * the API rejects those tokens at authoring time; it offers `{{last.*}}`
 * instead, the newest record matching the expected card and condition. An EVENT
 * rule has a trigger item and no "last". Offering a token the server will
 * refuse would be the editor promising something the platform does not do, so
 * the two lists are built separately and neither borrows from the other.
 *
 * WHY a tree. Flat, a real card's vocabulary is a long list where "Today + 7
 * days" sits between two elements of somebody's Staff card, and the dot-walked
 * entries multiply it. The shape the vocabulary already HAS — a few roots, each
 * with members, and lookups that lead somewhere else — is the shape the menu now
 * shows. Nothing about the tokens themselves changed: `{{today}}`,
 * `{{today+7d}}`, `{{now}}`, `{{last.<el>}}`, `{{last.<lookup>.<el>}}`,
 * `{{trigger.<el>}}`, `{{trigger.<lookup>.<el>}}`.
 *
 * Node shape:
 *   { token, label }                  a leaf; `label` is the PILL label
 *   { label, children: [...] }        a group; opens the next tier
 *   { token, label, children: [...] } a lookup: insertable AND walkable
 *   `menuLabel` overrides the text shown IN the menu, where the pill's full
 *   "Last record · Staff · Full name" path would only repeat its own trail.
 *
 * DOT-WALKING is one hop, deliberately — two segments after the namespace, the
 * API's own authoring cap. The API resolves `{{last.staff.name}}` by following
 * a card-lookup to the referenced item and stops there; a second hop would be a
 * query fan-out per token per notification.
 *
 * A lookup whose target card cannot be resolved (still loading, a card this
 * manager cannot read, a dangling reference) contributes NO children rather
 * than a guessed token: the alternative is a submenu that renders empty forever.
 */

const labelOf = element => element?.label?.singular || element?.name;

// A file element is never a message token (PR 10b): its value is an
// attachment id, which would land verbatim in somebody's email. Nor is a
// REPEATING GROUP (KPR-687): its value is a list of entries, which prints as
// nothing a person can read — an entry is read with `{{entry.<column>}}`, on a
// rule that watches the group's entries (buildEntryTokens).
const namedElements = elements => ( elements || [] ).filter( el => el?.name && el?.dataType !== 'file' && el?.dataType !== 'repeating-group' );

const isLookup = element => element?.dataType === 'card-lookup';

/** The date offsets the API's template grammar accepts: d / w / m, either sign. */
const TODAY_OFFSETS = [
	[ '+1d', '+ 1 day' ],
	[ '+7d', '+ 7 days' ],
	[ '+1w', '+ 1 week' ],
	[ '+1m', '+ 1 month' ],
	[ '-1d', '- 1 day' ],
	[ '-7d', '- 7 days' ],
	[ '-1w', '- 1 week' ],
	[ '-1m', '- 1 month' ],
];

/** "Today" and its arithmetic, as one drill-in group. */
function todayGroup() {
	return {
		label: 'Today',
		token: '{{today}}',
		children: [
			{ token: '{{today}}', label: 'Today', menuLabel: 'Today (exactly)' },
			...TODAY_OFFSETS.map( ( [ offset, words ] ) => ( {
				token: `{{today${ offset }}}`,
				label: `Today ${ words }`,
			} ) ),
		],
	};
}

/**
 * One namespace's elements as menu nodes. `namespace` is the token namespace
 * (`last`/`trigger`) and `title` the human word for it in a pill label.
 */
function namespaceChildren( namespace, title, elements, getElementsForCard ) {
	return namedElements( elements ).map( element => {
		const node = {
			token: `{{${ namespace }.${ element.name }}}`,
			label: `${ title } · ${ labelOf( element ) }`,
			menuLabel: labelOf( element ),
		};
		if ( ! isLookup( element ) ) return node;

		// A LOOKUP's own value is an ObjectId, and `{{last.staff}}` renders it
		// verbatim — deepTokens' terminalValue returns the element as stored —
		// so "507f1f77bcf86cd799439011" is what lands in somebody's email. What
		// an author means by "the staff member" is the linked record's TITLE,
		// and the API has a terminal for exactly that: `<ns>.<lookup>.title`
		// loads the referenced item's merged def and reads its title element.
		// So a lookup is offered as `.title`, never as the bare reference.
		const titleNode = {
			token: `{{${ namespace }.${ element.name }.title}}`,
			label: `${ title } · ${ labelOf( element ) } · Title`,
			menuLabel: `${ labelOf( element ) } (linked record's title)`,
		};

		const targetId = normalizeId( element?.options?.lookupCardId );
		const targetElements = ( targetId && getElementsForCard )
			? namedElements( getElementsForCard( targetId ) )
			: [];
		// A lookup whose target card cannot be enumerated still gets the title
		// terminal: authoring validates only the FIRST hop (that the name IS a
		// lookup on this card), which holds whether or not this client can read
		// the card it points at.
		if ( ! targetElements.length ) return { ...titleNode, menuLabel: labelOf( element ) };

		return {
			...titleNode,
			children: [
				titleNode,
				...targetElements.map( child => ( {
					token: `{{${ namespace }.${ element.name }.${ child.name }}}`,
					label: `${ title } · ${ labelOf( element ) } · ${ labelOf( child ) }`,
					menuLabel: labelOf( child ),
				} ) ),
			],
		};
	} );
}

/** Everything a SCHEDULED (expected-item) rule may template. */
export function buildScheduleTokens( expectedCardElements = [], getElementsForCard ) {
	const lastChildren = namespaceChildren( 'last', 'Last record', expectedCardElements, getElementsForCard );
	return [
		todayGroup(),
		{ token: '{{now}}', label: 'Now' },
		// Omitted entirely on a card with no elements: an empty submenu is worse
		// than no submenu.
		...( lastChildren.length ? [ { label: 'Last record', children: lastChildren } ] : [] ),
	];
}

/**
 * The FAN-OUT vocabulary: `{{each.*}}`, the item a create-item action is
 * making this copy FOR, and the source item a generator is generating from.
 *
 * Flat on purpose — no dot-walks. The API refuses `{{each.a.b}}` at authoring
 * because the loop resolves per target and a walk would cost a read per target
 * per token, so offering one here would promise something the platform
 * deliberately does not do.
 */
export function buildEachTokens( sourceCardElements = [] ) {
	const own = namedElements( sourceCardElements ).map( element => ( {
		token: `{{each.${ element.name }}}`,
		label: `Each item · ${ labelOf( element ) }`,
		menuLabel: labelOf( element ),
	} ) );
	const seen = new Set( own.map( node => node.token ) );
	const universal = [
		{ token: '{{each.id}}', label: 'Each item · Id', menuLabel: 'Id' },
		{ token: '{{each.title}}', label: 'Each item · Title', menuLabel: 'Title' },
	].filter( node => ! seen.has( node.token ) );
	const children = [ ...own, ...universal ];
	return children.length ? [ { label: 'Each matching item', children } ] : [];
}

/**
 * The OCCURRENCE vocabulary: `{{slot.*}}`, the moment a generator is writing a
 * record for. Four terminals, exactly the four the API resolves.
 */
export function buildSlotTokens() {
	return [ {
		label: 'This occurrence',
		children: [
			{ token: '{{slot.datetime}}', label: 'Occurrence · Date and time', menuLabel: 'Date and time' },
			{ token: '{{slot.date}}', label: 'Occurrence · Date', menuLabel: 'Date' },
			{ token: '{{slot.time}}', label: 'Occurrence · Time of day', menuLabel: 'Time of day' },
			{ token: '{{slot.label}}', label: 'Occurrence · Slot name', menuLabel: 'Slot name (e.g. Morning)' },
		],
	} ];
}

/** Everything a GENERATOR may template: the source item and the occurrence. */
export function buildGeneratorTokens( sourceCardElements = [] ) {
	return [
		...buildEachTokens( sourceCardElements ),
		...buildSlotTokens(),
		todayGroup(),
		{ token: '{{now}}', label: 'Now' },
	];
}

/**
 * The WATCHED-VALUE vocabulary a threshold rule adds: the reading that crossed
 * the bound. Three terminals, exactly the three the API resolves for this kind
 * — and they share the namespace the notification date watcher already owns,
 * because both are answering "what about this record came due". A field the
 * firing watcher does not fill renders '' with a run-row warning, so offering a
 * date watcher's `days` here would be promising something this kind never says.
 */
export function buildWatchTokens() {
	return [ {
		label: 'What crossed',
		children: [
			{ token: '{{watch.value}}', label: 'Crossing · The value', menuLabel: 'The value' },
			{ token: '{{watch.bound}}', label: 'Crossing · The bound', menuLabel: 'The bound it crossed' },
			{ token: '{{watch.element}}', label: 'Crossing · Which element', menuLabel: 'Which element' },
		],
	} ];
}

/** Everything a THRESHOLD rule may template: the record, plus what crossed. */
export function buildThresholdTokens( triggerCardElements = [], getElementsForCard ) {
	return [ ...buildWatchTokens(), ...buildTriggerTokens( triggerCardElements, getElementsForCard ) ];
}

/**
 * The PRESSED-VALUE vocabulary a card action adds (keepr-api KPR-382): the
 * inputs its button asks for, as `{{input.<key>}}`. A card-lookup input is not
 * offered here: its value is a record's id, which as text would print an id —
 * it feeds a lookup element (lookupSourceOptions), and nothing else. A date
 * input also offers the offsets the template grammar accepts.
 */
export function buildInputTokens( inputs = [] ) {
	const children = ( inputs || [] )
		.filter( input => input?.key && input?.dataType !== 'card-lookup' )
		.map( input => {
			const label = String( input.label || '' ).trim() || input.key;
			return { token: `{{input.${ input.key }}}`, label: `Asked · ${ label }`, menuLabel: label };
		} );
	return children.length ? [ { label: 'Asked when pressed', children } ] : [];
}

/** Everything an EVENT rule may template. */
export function buildTriggerTokens( triggerCardElements = [], getElementsForCard ) {
	const own = namespaceChildren( 'trigger', 'Trigger', triggerCardElements, getElementsForCard );
	// Deduped by token text: a card with an element literally named `id` or
	// `title` must not yield two menu entries for the same token.
	const seen = new Set( own.map( node => node.token ) );
	const universal = [
		{ token: '{{trigger.id}}', label: 'Trigger · Id', menuLabel: 'Id' },
		{ token: '{{trigger.title}}', label: 'Trigger · Title', menuLabel: 'Title' },
		// The last person to save the record (KPR-687): who ticked the entry,
		// for a "done by" user element or column.
		{ token: '{{trigger.updatedBy}}', label: 'Trigger · Last saved by', menuLabel: 'Last saved by' },
	].filter( node => ! seen.has( node.token ) );

	const triggerChildren = [ ...own, ...universal ];
	return [
		...( triggerChildren.length ? [ { label: 'Trigger item', children: triggerChildren } ] : [] ),
		todayGroup(),
		{ token: '{{now}}', label: 'Now' },
	];
}

// What `{{entry.*}}` answers besides a column (keepr-api template.js ENTRY_FIELDS).
const ENTRY_OWN_FIELDS = new Set( [ 'id', 'position', 'group' ] );

/**
 * The ENTRY vocabulary (KPR-687): `{{entry.*}}`, the entry of a repeating group
 * a rule that watches entries is running for — each of the group's columns,
 * the entry's place and the group's label. Flat, like `{{each.*}}`: the API
 * refuses a walk out of an entry. A file column never sits in a group, and a
 * calculated column reads like any other (its value is the entry's).
 *
 * `group` is the group element as the card serves it: `{ name, label,
 * options: { columns } }` (or the flat `columns` a schema read gives).
 */
export function buildEntryTokens( group ) {
	if ( ! group ) return [];
	const columns = Array.isArray( group?.options?.columns ) ? group.options.columns : ( Array.isArray( group?.columns ) ? group.columns : [] );
	const title = labelOf( group ) || 'Entry';
	// The entry's own fields answer before a column of the same name (keepr-api
	// template.js readEntryField), so a column named `id`, `position` or
	// `group` cannot be read by a token and is not offered as one.
	const own = namedElements( columns ).filter( column => ! ENTRY_OWN_FIELDS.has( column.name ) ).map( column => ( {
		token: `{{entry.${ column.name }}}`,
		label: `${ title } · ${ labelOf( column ) }`,
		menuLabel: labelOf( column ),
	} ) );
	const universal = [
		{ token: '{{entry.position}}', label: `${ title } · Place in the list`, menuLabel: 'Place in the list' },
		{ token: '{{entry.group}}', label: `${ title } · Group name`, menuLabel: 'Group name' },
		{ token: '{{entry.id}}', label: `${ title } · Id`, menuLabel: 'Id' },
	];
	return [ { label: 'This entry', children: [ ...own, ...universal ] } ];
}

// Flattening a tree back to `{token,label}` pairs lives with its only consumer,
// TokenInput's `flattenTokenNodes` — that is what builds the pill-label map,
// and one implementation of a walk is enough.
